<template>
  <div
    v-if="file.need_feedback"
    class="card"
  >
    <div class="card-body">
      <h4 class="">
        Need feedback
      </h4>
      <hr>

      <!-- Signaturit Flow -->
      <template v-if="file.use_signaturit">
        <!-- Signature Status -->
        <div
          v-if="file.signature_id && !file.signature_completed"
          class="mb-1"
        >
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="badge bg-light-info">
              <i data-feather="edit-3" class="me-50" /> Signature in progress
            </span>
            <a
              class="btn btn-sm btn-outline-primary"
              @click="checkSignatureStatus"
            >
              <i data-feather="eye" class="me-50" /> View status
            </a>
          </div>
        </div>

        <!-- Signed Document -->
        <div
          v-if="file.signature_completed"
          class="mb-1"
        >
          <span class="badge bg-light-success">
            <i data-feather="check-circle" class="me-50" /> Document signed
          </span>
          <a
            v-if="file.signed_document_url"
            class="btn btn-sm btn-outline-success mt-1 text-success w-100"
            :class="{ 'disabled': downloadingDocument }"
            @click="downloadSignedDocument"
          >
            <span v-if="downloadingDocument">
              <span class="spinner-border spinner-border-sm me-50" role="status" aria-hidden="true" />
              Downloading...
            </span>
            <span v-else>
              <i data-feather="download" class="me-50" /> Download signed document
            </span>
          </a>
        </div>

        <!-- Sign Button -->
        <div
          v-if="file.need_feedback_check && !file.signature_completed"
          class="mb-1"
        >
          <p class="text-muted mb-1">
            <i data-feather="info" class="me-50" />
            This document requires an electronic signature.
          </p>
          <a
            v-if="!file.signature_id"
            class="btn btn-primary"
            :class="{ 'disabled': signingLoading }"
            @click="openSignaturePage"
          >
            <span v-if="signingLoading">
              <span class="spinner-border spinner-border-sm me-50" role="status" aria-hidden="true" />
              Preparing signature...
            </span>
            <span v-else>
              <i data-feather="edit-3" class="me-50" /> Sign Document
            </span>
          </a>
        </div>
      </template>

      <!-- Regular File Upload Flow -->
      <template v-else>
        <div
          v-if="file.response"
          class="mb-1"
        >
          <table class="table table-condensed table-striped table-bordered">
            <tr class="border">
              <td class="border">
                {{ file.response.name }}<br><small>Uploaded: {{ file.response.created_at }}</small>
              </td>
              <td class="text-end border">
                <div class="row">
                  <div class="col-6">
                    <a
                      v-if="canUpload"
                      style="color: red;"
                      title="Delete file"
                      @click="deleteResponse(file.response.id)"
                    ><i data-feather="trash-2" /></a>
                  </div>
                  <div class="col-6">
                    <a
                      :href="file.response_url"
                      target="_blank"
                      title="View file"
                    ><i data-feather="eye" /></a>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>

        <span
          v-if="file.response && file.response.accepted === true"
          class="badge bg-light-success"
        >Document accepted</span>

        <span
          v-else-if="file.response && file.response.accepted === false"
          class="badge bg-light-danger"
        >Document rejected</span>

        <span
          v-else-if="file.response && file.response.accepted === null"
          class="badge bg-light-warning"
        >Pending to revision</span>

        <hr v-if="file.response">

        <template v-if="canUpload">
          <p class="text-muted mb-1">
            <small>
              1. Download the document · 2. Sign it in an external program (e.g. Signaturit) · 3. Upload the signed file for ICREA to validate.
            </small>
          </p>
          <div class="mb-1">
            <label
              for=""
              class="form-label"
            ><strong>Upload signed file</strong></label>
            <div
              class="input-group custom-file-button"
            >
              <label
                class="input-group-text"
                for="formFile"
              >Select file</label>
              <input
                id="formFile"
                :ref="`file-present-report`"
                class="form-control"
                type="file"
              >
            </div>
          </div>
          <a
            class="btn btn-small btn-primary"
            @click="upload"
          >Upload</a>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  components: {
  },
  data() {
    return {
      signingLoading: false,
      downloadingDocument: false,
    }
  },
  computed: {
    ...mapGetters({
      fields: 'modals/tableSettingsFields',
      file: 'cae/file',
      signatureData: 'signatures/data',
      filters: 'filters/filters',
    }),
    // filters.host is { id: undefined } when the view is deep-linked without ?host=, which would
    // drop host_id from the payload and leave the backend guessing the institution from the
    // logged-in user. Fall back to the route query, and never send a non-numeric id.
    hostId() {
      const id = (this.filters.host ? this.filters.host.id : null) ?? this.$route.query.host
      return id !== null && id !== undefined && id !== '' && !Number.isNaN(Number(id)) ? Number(id) : null
    },
    // The response can be (re)uploaded only while it is still open: no response yet,
    // pending revision (accepted === null) or rejected (accepted === false). Once the
    // admin accepts it the document is locked — allowing another upload would silently
    // reset it back to pending on the backend.
    canUpload() {
      return this.file.need_feedback_check && (!this.file.response || this.file.response.accepted !== true)
    },
  },
  mounted() {

  },
  methods: {
    async openSignaturePage() {
      if (this.signingLoading) return

      this.signingLoading = true

      try {
        // Request signature URL from backend
        const resp = await Vue.prototype.$http.post(`${Vue.prototype.$groupUrl}/cae/request-signature`, {
          file_id: this.file.id,
          host_id: this.filters.host ? this.filters.host.id : null,
        })

        if (resp.data && resp.data.signature_url) {
          // Open Signaturit signing page in a new window
          window.open(resp.data.signature_url, '_blank', 'width=800,height=600')

          // Refresh file data to get updated signature status
          setTimeout(async () => {
            await this.$store.dispatch('cae/fetchFile', { filters: this.filters, id: this.file.id })
            feather.replace({
              width: 14,
              height: 14,
            })
          }, 1000)
        } else {
          this.$toastr.error('', 'Could not get signature URL')
        }
      } catch (e) {
        console.error('Error requesting signature:', e)
        this.$toastr.error('', 'Error requesting signature. Please try again.')
      } finally {
        this.signingLoading = false
      }
    },
    async checkSignatureStatus() {
      if (this.file.signature_id) {
        this.$store.dispatch('modals/toggleCheckSignatureStatus', true)
        await this.$store.dispatch('signatures/fetchData', this.file.signature_id)
        window.open(this.signatureData[0].url, '_blank', 'width=800,height=600')
      }
    },
    async downloadSignedDocument() {
      if (this.downloadingDocument || !this.file.signed_document_url) return

      this.downloadingDocument = true

      try {
        const resp = await Vue.prototype.$http.get(this.file.signed_document_url, {
          responseType: 'arraybuffer',
        })

        // Create a blob from the response data
        const blob = new Blob([resp.data], { type: 'application/pdf' })

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob)

        // Create a link and set its href to the blob URL
        const link = document.createElement('a')
        link.href = url

        // Set the download filename
        const filename = this.file.name_en || this.file.name_cat || 'signed_document'
        link.setAttribute('download', `${filename}_signed.pdf`)

        // Append the link to the body (required for Firefox)
        document.body.appendChild(link)

        // Trigger a click on the link to start the download
        link.click()

        // Cleanup: Remove the link and revoke the object URL
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Error downloading signed document:', e)
        this.$toastr.error('', 'Error downloading document. Please try again.')
      } finally {
        this.downloadingDocument = false
      }
    },
    async deleteResponse(id) {
      Vue.swal({
        title: 'Do you want to delete the feedback response?',
        html: '',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Yes',
      }).then(async result => {
        if (result.isConfirmed) {
          await this.$store.dispatch('cae/deleteResponse', { filters: this.filters, id, file: this.file.id })
        }
      })
    },
    async upload() {
      const file = this.$refs['file-present-report'].files[0]

      if (!file) {
        this.$toastr.error('', 'Please, upload a file first')
      } else {
        await this.$store.dispatch('cae/uploadResponse', {
          filters: this.filters, response: file, file: this.file.id, host_id: this.hostId,
        })
      }

      setTimeout(() => {
        feather.replace({
          width: 14,
          height: 14,
        })
      }, 100)
    },
  },
}
</script>
